import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LoaderCircle, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { Reveal, MaskedLine } from '@/components/Reveal';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/data/products';
import { useProducts } from '@/lib/shopify';
import { WishlistButton } from '@/components/WishlistButton';

const catalogStatus = ({ loading, source }) => {
  if (loading) return 'Syncing live Shopify catalog…';
  if (source === 'shopify') return '● Live shop — synced from Shopify';
  return '○ Live sync unavailable — showing preview';
};

const catalogStatusColor = ({ error, source }) => {
  if (error) return 'text-[#8A3542]';
  if (source === 'shopify') return 'text-[#657260]';
  return 'text-[#777]';
};

const collectionCount = (category, products) => (
  category.productCount ?? products.filter((product) => product.collections?.some((item) => item.handle === category.handle)).length
);

function CollectionFilterButton({ category, active, count, onClick, mobile = false }) {
  return <button
    type="button"
    data-testid={`${mobile ? 'mobile-' : ''}collection-filter-${category.handle}`}
    onClick={onClick}
    className={`min-h-11 border px-5 py-2.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.18em] transition-[background-color,border-color,color,transform] duration-300 active:scale-[0.98] ${mobile ? 'w-full text-left' : ''} ${active ? 'border-[#1E1E1E] bg-[#1E1E1E] text-white' : 'border-[#D1D1D6] bg-white/80 text-[#5C5C5C] hover:border-[#8A9A86] hover:text-[#1E1E1E]'}`}
  >
    {category.title} <span className="ml-2 opacity-55">{count}</span>
  </button>;
}

function MobileFilterSheet({ categories, collection, products, onSelect }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(collection);
  useEffect(() => {
    if (open) setPending(collection);
  }, [collection, open]);
  return <div className="md:hidden">
    <button type="button" onClick={() => setOpen(true)} data-testid="mobile-filter-button" className="fixed bottom-5 left-1/2 z-40 flex min-h-12 -translate-x-1/2 items-center gap-3 border border-[#1E1E1E] bg-[#1E1E1E] px-5 font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_28px_rgba(30,30,30,0.2)] transition-[background-color,transform] duration-200 active:scale-95">
      Filter <span className="border-l border-white/30 pl-3 text-[#C7D1C3]">{collectionCount(categories.find((item) => item.handle === collection) || categories[0], products)}</span>
    </button>
    <AnimatePresence>{open && <>
      <motion.button type="button" aria-label="Close collection filters" onClick={() => setOpen(false)} data-testid="mobile-filter-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[#1E1E1E]/35 backdrop-blur-sm" />
      <motion.section role="dialog" aria-modal="true" aria-label="Collection filters" data-testid="mobile-filter-sheet" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 280 }} className="fixed inset-x-0 bottom-0 z-[51] max-h-[78dvh] overflow-y-auto border-t border-[#D1D1D6] bg-[#FAFAFA]/95 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-4"><p className="font-syne text-lg font-bold uppercase">Shop collection</p><button type="button" onClick={() => setOpen(false)} data-testid="mobile-filter-close" className="grid h-11 w-11 place-items-center border border-[#D1D1D6] font-mono2 text-lg transition-[background-color,color] duration-200 active:bg-[#1E1E1E] active:text-white" aria-label="Close collection filters">×</button></div>
        <div className="mt-6 grid gap-2" data-testid="mobile-filter-options">{categories.map((category) => <CollectionFilterButton key={category.handle} category={category} active={pending === category.handle} count={collectionCount(category, products)} onClick={() => setPending(category.handle)} mobile />)}</div>
        <button type="button" onClick={() => { onSelect(pending); setOpen(false); }} data-testid="mobile-filter-apply" className="mt-5 flex min-h-12 w-full items-center justify-center bg-[#1E1E1E] px-5 font-syne text-xs font-bold uppercase tracking-[0.16em] text-white transition-[background-color,transform] duration-200 active:scale-[0.98] active:bg-[#657260]">Show pieces</button>
      </motion.section>
    </>}</AnimatePresence>
  </div>;
}

function ProductCard({ product, index }) {
  const { add, setOpen } = useCart();
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div className="group" data-testid={`product-card-${product.id}`}>
        <div className="relative overflow-hidden bg-[#F0F1EE]" data-cursor>
          <Link to={`/products/${product.id}`} data-testid={`product-image-link-${product.id}`} className="block">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="aspect-[3/4] w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
          </Link>
          <span className="absolute left-3 top-3 border border-[#D1D1D6] bg-white/90 px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#1E1E1E]">
            {product.tag}
          </span>
          <WishlistButton productId={product.id} compact className="absolute right-3 top-3 z-10" />
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={(e) => {
              e.stopPropagation();
              add(product);
              toast.success(`${product.name} added to cart`);
              setOpen(true);
            }}
            className="absolute bottom-3 right-3 z-10 flex h-11 w-11 items-center justify-center bg-[#1E1E1E] text-white transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#8A9A86]"
            aria-label={`Quick add ${product.name}`}
          >
            <Plus size={16} />
          </button>
        </div>
        <Link to={`/products/${product.id}`} className="block w-full text-left" data-testid={`product-view-${product.id}`}>
          <div className="flex items-baseline justify-between pt-4">
            <h3 className="font-syne text-sm font-bold uppercase transition-colors group-hover:text-[#657260]" data-testid={`product-name-${product.id}`}>
              {product.name}
            </h3>
            <p className="font-mono2 text-sm text-[#5C5C5C]" data-testid={`product-price-${product.id}`}>{formatPrice(product.price, product.currency)}</p>
          </div>
          <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#777]">
            {product.color}
          </p>
        </Link>
      </div>
    </Reveal>
  );
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [collection, setCollection] = useState(() => searchParams.get('collection') || 'all');
  const { products, collections, source, loading, error } = useProducts();

  const categories = useMemo(
    () => [{ handle: 'all', title: 'All', productCount: products.length }, ...collections],
    [collections, products.length]
  );

  const filtered = useMemo(
    () => (collection === 'all' ? products : products.filter((product) => product.collections?.some((item) => item.handle === collection))),
    [collection, products]
  );
  const selectCollection = (handle) => {
    setCollection(handle);
    setSearchParams(handle === 'all' ? {} : { collection: handle });
  };

  return (
    <div className="aura-page-surface relative min-h-screen text-[#1E1E1E]" data-testid="shop-page">
      <div className="mx-auto max-w-[1500px] px-5 pb-28 pt-36 sm:px-8 lg:px-12 lg:pt-44">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.4em] text-[#777]">
          Current drop — {String(filtered.length).padStart(2, '0')} pieces
        </p>
        <h1 className="mt-4 font-syne text-4xl font-extrabold uppercase leading-[0.9] sm:text-5xl lg:text-6xl" data-testid="shop-title">
          <MaskedLine delay={0.1}>Pick your</MaskedLine>
          <MaskedLine delay={0.2}>
            <span className="sage-text">favorite.</span>
          </MaskedLine>
        </h1>
        <p
          className={`mt-4 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.3em] ${catalogStatusColor({ error, source })}`}
          data-testid="catalog-source-badge"
          role="status"
        >
          {loading && <LoaderCircle size={12} className="animate-spin" />}
          {catalogStatus({ loading, source })}
        </p>

        <div className="mt-14 hidden flex-wrap gap-2 md:flex" data-testid="category-filters">
          {categories.map((category) => (
            <CollectionFilterButton key={category.handle} category={category} active={collection === category.handle} count={collectionCount(category, products)} onClick={() => selectCollection(category.handle)} />
          ))}
        </div>

        <div id="collections" className="mt-10 grid grid-cols-2 gap-x-3 gap-y-12 pb-16 md:mt-16 md:grid-cols-3 md:gap-x-5 md:pb-0 lg:grid-cols-4" data-testid="product-grid">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
      <MobileFilterSheet categories={categories} collection={collection} products={products} onSelect={selectCollection} />

      <Footer />
    </div>
  );
}
